import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { fmtPnl, cn, genClientRequestId } from '../../../lib/utils';
import { Spinner, ErrorState, ConfirmModal } from '../../../components/ui';
import { toast } from '../../../store';
import { useLtpStore } from '../../../store/ltpStore';
import {
  useManualTradingWatchlist,
  useUpdateManualTradingWatchlistRow,
  useDeleteManualTradingWatchlistRow,
  useManualPaperOrderbook,
  useManualPaperPositions,
  useExitManualTradingPosition,
  usePrepareManualTradingExit,
  useConfirmManualTradingExit,
  usePrepareManualTradingCancel,
  useConfirmManualTradingCancel,
  useManualRiskTriggers,
  useUpdateManualActiveRisk,
  useManualTradeBook,
  useEnableMasterStrategy,
  QK,
} from '../../../api/hooks';
import { manualTradingApi } from '../../../api/client';
import { StrategyStatusBadge, MetaChip } from './ManualBadges';
import WatchlistTable from './WatchlistTable';
import InlineAddSymbolSearch from './InlineAddSymbolSearch';
import OrdersTable from './OrdersTable';
import PositionsTable from './PositionsTable';
import TradeBookTable from './TradeBookTable';
import ManualFanoutPanel from './ManualFanoutPanel';
import ExitAllModal from './modals/ExitAllModal';
import ConfirmManualExitModal from './modals/ConfirmManualExitModal';
import ConfirmManualCancelOrderModal from './modals/ConfirmManualCancelOrderModal';
import ConfirmBrokerSyncModal from './modals/ConfirmBrokerSyncModal';
import { classifyManualExitResult, classifyManualCancelResult, extractOrderErrorMessage, normalizeMode } from './utils/manualSignalResult';
import { normalizeRiskUnit } from './riskUnits';
import {
  friendlyTradeIn, friendlySegment, friendlyProductType, friendlyQtyMode, optionMappingSummary,
} from './manualLabels';
// Reuse the GLOBAL Paper Orderbook / Position Book mappers so Manual tabs render identical fields & P&L.
import { computeUnrealizedPnl, normalizeOrderRow, normalizePositionRow } from '../../broker/bookUtils';

// Current date in IST (YYYY-MM-DD) — explicit Asia/Kolkata so UTC conversion can't shift the date.
function istToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

// Editable frontend field → backend column (only mutable fields are PATCHed).
const FE_TO_BE = {
  qtyValue: 'qty_value',
  marginValue: 'margin_value',
  orderType: 'order_type',
  mpp: 'mpp',
  riskReferenceMode: 'risk_reference_mode',
  target: 'target',
  stopLoss: 'stop_loss',
  status: 'status',
};

// Backend watchlist row → frontend row shape (+ ephemeral local BUY/SELL overlay).
function mapWatchlistRow(r, overrides) {
  const ov = overrides?.[`${r.deployment_id}:${r.id}`] || {};
  return {
    id: r.id,
    backendId: r.id,
    deploymentId: r.deployment_id,
    strategyId: r.strategy_id,
    symbol: r.symbol,
    displaySymbol: r.display_symbol || r.symbol,
    exchange: r.exchange,
    segment: r.segment,
    qtyValue: r.qty_value ?? '',
    marginValue: r.margin_value ?? '',
    orderType: r.order_type || 'Market',
    mpp: r.mpp ?? '0',
    target: r.target ?? '0',
    stopLoss: r.stop_loss ?? '0',
    riskUnit: normalizeRiskUnit(r.risk_unit), // persisted unit; missing → PERCENT
    riskReferenceMode: String(r.risk_reference_mode || 'OPTION').trim().toUpperCase() === 'BASE' ? 'BASE' : 'OPTION',
    status: r.status || 'Ready',
    ltp: null, // live LTP merged in below; null → renders "—" until a tick arrives
    // Last Signal / Last Trigger (F2): the immediate local overlay wins until the backend-persisted
    // values (F1: last_signal_action / last_trigger_price / last_trigger_at) catch up, after which the
    // overlay is cleared (see reconciliation effect) and the backend becomes the source of truth. So on
    // login/reload/remount (no overlay), persisted backend values display directly; legacy nulls → "—".
    lastSignal: ov.lastSignal ?? r.last_signal_action ?? null,
    lastTriggerPrice: ov.lastTrigger ?? r.last_trigger_price ?? null,
    lastTriggerAt: r.last_trigger_at ?? null,
  };
}

// Backend PAPER position dict → Position Book row (via the shared global mapper) + Manual extras.
// Mirrors the global Paper Position Book's live-LTP→unrealized merge: backend carries NO ltp/unrealized,
// so for OPEN rows we overlay live LTP from useLtpStore and recompute unrealized exactly as the global page.
// symbolToRowId maps the position back to its watchlist row for EXIT. Quantities are shown as the backend
// returns them (e.g. MCX lot-converted 1000) — no frontend lot math.
// currentByRowId/currentBySymbol are current (ACTIVE + PROCESSING) risk-trigger lookups (E2.2): the
// Active Target / Active SL columns come ONLY from the trigger — never from the watchlist row's
// future-entry Target/SL. A PROCESSING trigger keeps Target/SL visible but locks editing/Exit
// (an in-flight risk exit) — it is NOT hidden.
function buildPositionRow(p, index, ltpMap, symbolToRowId, currentByRowId, currentBySymbol) {
  const norm = normalizePositionRow(p, index); // { symbol, product, buyQty, buyAvg, sellQty, sellAvg, netQty, ltp, realizedPnl, unrealizedPnl, ... }
  const sym = String(norm.symbol || '').trim().toUpperCase();
  let { ltp, unrealizedPnl } = norm;
  const live = ltpMap?.[sym];
  if (live != null && Number.isFinite(Number(live)) && norm.netQty !== 0) {
    ltp = Number(live);
    unrealizedPnl = computeUnrealizedPnl(norm, ltp);
  }
  const isClosed = Boolean(p.is_closed) || norm.netQty === 0;
  const backendRowId = p.watchlist_row_id ?? p.manual_watchlist_row_id ?? null;
  // Preliminary row id from the position response / base-symbol map (existing behavior).
  const preExitRowId = backendRowId != null ? backendRowId : (symbolToRowId?.[sym] ?? null);
  const positionSymbol = String(p.resolved_option_symbol || p.position_symbol || norm.symbol || '').trim().toUpperCase();
  const resolvedOptionSymbol = p.resolved_option_symbol || null;
  const backendTrig = (p.active_trigger_id != null || p.active_risk_status != null)
    ? {
        id: p.active_trigger_id ?? null,
        status: String(p.active_risk_status || '').toUpperCase() || null,
        watchlist_row_id: backendRowId,
        target_enabled: !!p.active_target_enabled,
        target_trigger_price: p.active_target ?? null,
        stop_loss_enabled: !!p.active_sl_enabled,
        stop_loss_trigger_price: p.active_sl ?? null,
        updated_at: p.active_risk_updated_at ?? null,
      }
    : null;

  // Current risk trigger: match by resolved option symbol first, then embedded backend trigger, then
  // watchlist_row_id, then base symbol. Maps hold ACTIVE + PROCESSING (non-terminal) triggers only.
  const trig = currentBySymbol?.[positionSymbol] || backendTrig || (preExitRowId != null && currentByRowId?.[preExitRowId]) || currentBySymbol?.[sym] || null;
  // Additive fallback: LIVE dynamic-option positions may carry no row id and their resolved option
  // symbol won't match the base/index watchlist symbol — reuse the matched trigger's watchlist_row_id.
  const exitRowId = preExitRowId != null ? preExitRowId : (trig?.watchlist_row_id ?? null);
  const activeRiskProcessing = String(trig?.status || '').toUpperCase() === 'PROCESSING';
  const canSelfHealDynamicOptionRisk = !trig && p.manual_managed === true && !!resolvedOptionSymbol && exitRowId != null && !!positionSymbol;
  return {
    ...norm,
    ltp,
    unrealizedPnl,
    isClosed,
    status: isClosed ? 'Closed' : 'Open',
    exitRowId,
    positionSymbol,
    // Row-level broker Sync identity (PREPARE/CONFIRM): the opening broker order log id for this
    // position cycle, when the backend has attributed one. Surfaced as-is, never derived here.
    entryBrokerOrderLogId: p.entry_broker_order_log_id ?? null,
    // Position-opening chronology for display ordering only (E — newest-first). Backend already
    // computes this on every position-building path (PAPER intraday fills, PAPER positional
    // snapshot, Manual LIVE confirmed fills) as the active cycle's first-fill placed_at; simply
    // passed through here, never recomputed on the frontend.
    openedAt: p.opened_at ?? null,
    sourceSymbol: p.source_symbol || p.base_symbol || null,
    resolvedOptionSymbol,
    manualManaged: p.manual_managed === true || exitRowId != null,
    canExit: !isClosed && Math.abs(norm.netQty) > 0 && exitRowId != null,
    // E2.2 active-risk fields (from the trigger only; ACTIVE or PROCESSING)
    activeTargetPrice: trig?.target_enabled ? trig.target_trigger_price : null,
    activeStopLossPrice: trig?.stop_loss_enabled ? trig.stop_loss_trigger_price : null,
    activeTargetEnabled: !!trig?.target_enabled,
    activeStopLossEnabled: !!trig?.stop_loss_enabled,
    activeRiskTriggerId: trig?.id ?? null,
    activeRiskUpdatedAt: trig?.updated_at ?? null,
    // A current trigger exists (ACTIVE or PROCESSING) → keep Target/SL visible even when locked.
    hasCurrentRisk: !!trig,
    // PROCESSING = in-flight risk exit → show read-only values, block edits and duplicate Exit.
    activeRiskProcessing,
    exitProcessing: !isClosed && activeRiskProcessing,
    canEditActiveRisk: !isClosed && exitRowId != null && (!!trig || canSelfHealDynamicOptionRisk) && !activeRiskProcessing,
  };
}

function normalizeDeleteSymbol(value) {
  return String(value || '').trim().toUpperCase();
}

function isOpenManualPosition(position) {
  return !position?.isClosed && Math.abs(Number(position?.netQty) || 0) > 0;
}

function collectDeleteMatchSymbols(row) {
  return new Set(
    [row?.symbol, row?.sourceSymbol, row?.resolvedOptionSymbol, row?.positionSymbol, row?.baseSymbol]
      .map(normalizeDeleteSymbol)
      .filter(Boolean),
  );
}

function collectPositionMatchSymbols(position) {
  return new Set(
    [position?.sourceSymbol, position?.resolvedOptionSymbol, position?.positionSymbol, position?.symbol]
      .map(normalizeDeleteSymbol)
      .filter(Boolean),
  );
}

function findBlockingOpenPositionForWatchlistRow(row, openPositions = []) {
  if (!row) return null;
  const rowId = row.backendId ?? row.id;
  const open = openPositions.filter(isOpenManualPosition);
  if (rowId != null) {
    const rowIdText = String(rowId);
    const byId = open.find((position) => position?.exitRowId != null && String(position.exitRowId) === rowIdText);
    if (byId) return byId;
  }
  const rowSymbols = collectDeleteMatchSymbols(row);
  if (rowSymbols.size === 0) return null;
  return open.find((position) => {
    const positionSymbols = collectPositionMatchSymbols(position);
    for (const symbol of rowSymbols) {
      if (positionSymbols.has(symbol)) return true;
    }
    return false;
  }) || null;
}

// Mirrors the local useIsMobile() pattern established in Strategies.jsx (M2C1) — a JS-level
// gate for logic decisions (which onClick branch to take), not just CSS visibility.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia?.('(max-width: 1023.98px)').matches ?? false
  );
  useEffect(() => {
    const mql = window.matchMedia?.('(max-width: 1023.98px)');
    if (!mql) return;
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

// Row-level broker Sync — invalidate the queries a confirmed sync can change (active AND inactive,
// so a collapsed card refetches when expanded). Deliberately excludes the orderbook query: broker
// Sync never imports/creates an order the Manual Orders tab would show.
function invalidateManualBrokerSyncQueries(qc, deploymentId) {
  qc.invalidateQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId) });
  qc.invalidateQueries({ queryKey: QK.manualRiskTriggers(deploymentId) });
  qc.invalidateQueries({ queryKey: ['manualTradeBook', deploymentId], exact: false });
  qc.invalidateQueries({ queryKey: QK.manualTradingWatchlist(deploymentId) });
}

// Await only the queries that are currently active (enabled/mounted) so the modal stays in its
// submitting state until the visible data settles. Inactive queries were already invalidated above
// and will refetch on enable. Returns a promise so the caller can gate `finally` on settlement.
function refetchActiveManualBrokerSyncQueries(qc, deploymentId) {
  return Promise.all([
    qc.refetchQueries({ queryKey: QK.paperPositionsByDeployment(deploymentId), type: 'active' }),
    qc.refetchQueries({ queryKey: QK.manualRiskTriggers(deploymentId), type: 'active' }),
    qc.refetchQueries({ queryKey: ['manualTradeBook', deploymentId], exact: false, type: 'active' }),
    qc.refetchQueries({ queryKey: QK.manualTradingWatchlist(deploymentId), type: 'active' }),
  ]);
}

// Map a single reconcile-broker row result to EXACTLY ONE user-facing toast (no raw payloads).
// Only called for TERMINAL statuses — the 3 confirmation-required statuses open a modal instead
// (see syncPosition) and are never passed here.
function toastForBrokerSyncStatus(status, message) {
  switch (status) {
    case 'IN_SYNC':
      toast.success('Position is already in sync with your broker.');
      break;
    case 'SYNC_APPLIED':
    case 'ALREADY_SYNCED':
    case 'IMPORTED_EXTERNAL_EXIT':
      toast.success(message || 'Broker sync is complete.');
      break;
    case 'STALE_CONFIRMATION':
      toast.error(message || 'Broker position changed before confirmation. Please sync again.');
      break;
    case 'UNRESOLVED_MANUAL_REVIEW':
      toast.warn(message || 'Broker sync could not safely match this position. No changes were made.');
      break;
    case 'BROKER_UNAVAILABLE':
      toast.error(message || 'Unable to sync with the broker right now. Please try again.');
      break;
    case 'PROJECTION_RETRY_REQUIRED':
      toast.warn(message || 'Broker sync could not finish updating AlgoSys. Please try again.');
      break;
    case 'NO_OPEN_LOCAL_POSITION':
      toast.info(message || 'No open Manual position was found to sync.');
      break;
    case 'BROKER_STILL_OPEN':
      toast.info(message || 'Broker position is still open. No changes were made.');
      break;
    default:
      // Unknown status must not imply success.
      toast.info(message || 'Broker sync completed with no changes.');
  }
}

// Tab button styled as a raised, app/browser-style tab (not a plain underline) — sized as a real
// tap target (min 44px) with hover/focus affordance. Both active/inactive states keep the exact
// same border geometry (x:1px, top:2px, bottom:0) so switching tabs never shifts layout; only
// background/border-color/text-color differ. Mobile stays compact via responsive prefixes only
// (no separate mobile markup). `count` is the existing muted all-rows badge (Orders/Trade Book);
// `badgeCount` is the solid-red open-Positions count — kept as a separate prop so only the
// Positions badge gets the red "needs attention" treatment.
function TabButton({ active, onClick, label, count, badgeCount }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2.5 sm:px-3 min-h-[44px] text-xs sm:text-sm font-mono font-semibold',
        'rounded-t sm:rounded-t-md border-x border-t-2 border-b-0 -mb-px',
        'transition-colors whitespace-nowrap shrink-0 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-1 focus-visible:ring-offset-bg-surface',
        active
          ? 'bg-bg-elevated border-bg-border border-t-amber text-txt-primary'
          : 'border-transparent text-txt-secondary hover:text-txt-primary hover:bg-bg-hover/40 hover:border-bg-border/50',
      )}
    >
      {label}
      {badgeCount != null && badgeCount > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-bear text-white text-[10px] font-bold leading-none',
            badgeCount > 99 ? 'h-[18px] min-w-[22px] px-1' : 'h-[18px] w-[18px]',
          )}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
      {count != null && (
        <span className="mt-count px-1.5 py-0.5 rounded bg-bg-hover text-2xs">{count}</span>
      )}
    </button>
  );
}

export default function ManualStrategyCard({
  strategy,
  collapsed,
  onToggle,
  onBuy,
  onSell,
  onExit,
  onAddSymbol,
  onAddSymbolSelect,
  onCancelOrder,
  onEditPositionRisk,
  signalOverrides,
  onClearSignalOverride,
  canManageFanout = false,
}) {
  const isMobile = useIsMobile();
  // Mobile shows the inline Add Symbol search directly (always expanded above the watchlist).
  // Desktop continues to use the existing SymbolPickerModal via onAddSymbol, untouched.
  // Mobile-only: symbol just added via inline search, used to scroll/highlight its row once
  // the watchlist refetch lands it in `rows`. Never set for a duplicate (already-present) add.
  const [pendingAddedSymbol, setPendingAddedSymbol] = useState(null);
  // Default tab: Positions — open positions are what users look for first; Orders/Trade Book
  // remain one click away. Never auto-switched by BUY/SELL/exit actions.
  const [tab, setTab] = useState('positions');
  // Risk Unit: card-level default derived from persisted watchlist rows.
  // Row pending a Remove Symbol confirmation (M5) — set by requestDeleteRow, cleared on
  // confirm/cancel. Holds the full row so the eventual deleteRow(row) call is unchanged.
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [enableMasterConfirmOpen, setEnableMasterConfirmOpen] = useState(false);

  const mt = strategy.manual_trading || {};
  const isMasterManual = String(strategy.strategyMode || '').toUpperCase() === 'MASTER_MANUAL';
  const deploymentRole = String(strategy.deploymentRole || '').toUpperCase();
  const isMasterParent = deploymentRole === 'MASTER_PARENT';
  const isFanoutChild = deploymentRole === 'FANOUT_CHILD';
  // Resolved execution mode (PAPER|LIVE) — single normalization point, reused below.
  const executionMode = normalizeMode(strategy.executionMode) || 'PAPER';
  // Only FANOUT_CHILD (deployment_role) linked via parent_deployment_id can represent a
  // mapped/subscriber card reaching this component — DeploymentRole has no separate value for it.
  const isMappedOrSubscriber = isFanoutChild || (strategy.parentDeploymentId ?? null) != null;
  const enableMasterMut = useEnableMasterStrategy();
  const isOption = String(mt.trade_in || '').toUpperCase() === 'OPTION';
  const mappingSummary = isOption ? optionMappingSummary(strategy.execution) : null;

  // ── Row-level broker Sync (reconcile-broker PREPARE/CONFIRM) — normal Manual LIVE only ──
  const qc = useQueryClient();
  const reconcileBrokerMut = useMutation({
    mutationFn: ({ deploymentId: depId, body }) => manualTradingApi.reconcileBroker(depId, body),
    retry: false,
  });

  // ── Watchlist persistence (per deployment, active only) ──
  const deploymentId = strategy.deploymentId;
  const isActive = String(strategy.deploymentStatus || '').toUpperCase() === 'ACTIVE';
  const wlQ = useManualTradingWatchlist(deploymentId, { enabled: isActive });
  const updateMut = useUpdateManualTradingWatchlistRow();
  const deleteMut = useDeleteManualTradingWatchlistRow();

  // Reconcile the immediate Last Signal/Trigger overlay with backend-persisted values (F2): once a
  // watchlist refetch carries non-null last_signal_action + last_trigger_price for an overlaid row,
  // drop that overlay so the backend becomes the long-term source of truth. Runs ONLY when the backend
  // data actually changes (dep: wlQ.data) and reads the latest overlays via a ref — so setting a fresh
  // overlay never triggers a premature clear (no flicker on a 2nd entry).
  const overlaysRef = useRef(signalOverrides);
  overlaysRef.current = signalOverrides;
  useEffect(() => {
    const ov = overlaysRef.current || {};
    for (const r of (wlQ.data || [])) {
      const key = `${r.deployment_id}:${r.id}`;
      if (ov[key] && r.last_signal_action != null && r.last_trigger_price != null) {
        onClearSignalOverride?.(r.deployment_id, r.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wlQ.data]);

  // Card-level Risk Unit is the persisted value derived from rows (first row carrying risk_unit,
  // else PERCENT). Add Symbol still uses this as its default for newly added symbols.
  const persistedRiskUnit = useMemo(
    () => normalizeRiskUnit((wlQ.data || []).find((r) => r.risk_unit != null)?.risk_unit),
    [wlQ.data],
  );
  const riskUnit = persistedRiskUnit;

  // Backend-confirmed (mixed POINTS/PERCENT rows regression-tested safe): change a SINGLE row's
  // risk_unit only. Sends { risk_unit } exclusively — never target/stop_loss, so existing numeric
  // values are never cleared/converted/rewritten by a unit change.
  const changeRowRiskUnit = (row, newUnit) => {
    if (!row.backendId || normalizeRiskUnit(row.riskUnit) === newUnit) return; // no-op / dedupe
    updateMut.mutate(
      { rowId: row.backendId, deploymentId, payload: { risk_unit: newUnit } },
      { onError: (e) => toast.error(e?.message || 'Failed to update risk unit.') },
    );
  };

  // ── PAPER Orders / Open Positions for THIS deployment (backend deployment_id-filtered) ──
  // Read-only views load whenever the card is expanded (not only when ACTIVE) so an expanded STOPPED
  // deployment can still show its Orders/Positions/Risk/Trade Book and expose risk-reducing Exit All.
  // BUY/SELL/watchlist entry stays gated on `isActive` (see `wlQ`). Collapsed → all read queries disabled.
  const tabsEnabled = !collapsed;
  const ordersQ = useManualPaperOrderbook(deploymentId, { enabled: tabsEnabled });
  const positionsQ = useManualPaperPositions(deploymentId, { enabled: tabsEnabled });
  const riskTriggersQ = useManualRiskTriggers(deploymentId, { enabled: tabsEnabled });

  // ── Live LTP (reuse the shared /api/ws/ltp store; ref-counted) ──
  const ltpMap = useLtpStore((s) => s.ltp);
  // Additive `type:"quote"` data (change_percent, etc.) — separate map, watchlist-only overlay
  // below; Positions' buildPositionRow intentionally does not consume this.
  const quotesMap = useLtpStore((s) => s.quotes);
  const subscribe = useLtpStore((s) => s.subscribe);
  const unsubscribe = useLtpStore((s) => s.unsubscribe);
  // Derive symbols from the RAW query data (not the priced rows) so the list is stable across price
  // ticks. Manual Dynamic Option rows must subscribe to the executed option symbol, not just the base
  // watchlist/index symbol.
  const watchedSymbols = useMemo(
    () => Array.from(new Set(
      [
        ...(wlQ.data || []).map((r) => r.symbol),
        ...(positionsQ.data || []).map((p) => p.resolved_option_symbol || p.position_symbol || p.symbol),
      ].map((symbol) => String(symbol || '').trim().toUpperCase()).filter(Boolean),
    )),
    [wlQ.data, positionsQ.data],
  );
  const watchedKey = JSON.stringify(watchedSymbols);
  useEffect(() => {
    if (watchedSymbols.length === 0) return undefined;
    subscribe(watchedSymbols);
    return () => unsubscribe(watchedSymbols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedKey]);

  // Symbol → watchlist row backend id (first wins) — used to map a position back to its row for EXIT.
  const symbolToRowId = useMemo(() => {
    const m = {};
    for (const r of (wlQ.data || [])) {
      const k = String(r.symbol || '').trim().toUpperCase();
      if (k && r.id != null && !(k in m)) m[k] = r.id;
    }
    return m;
  }, [wlQ.data]);

  // ── Trade Book (backend-reconstructed trade cycles) — filters/pagination owned here so the query key
  // stays stable; polling runs ONLY for the current IST date while the Trade Book tab is active. ──
  const [tbDate, setTbDate] = useState(istToday);
  const [tbStatus, setTbStatus] = useState('ALL');
  const [tbSymbol, setTbSymbol] = useState(''); // '' = All Symbols (param omitted)
  const [tbPage, setTbPage] = useState(1);
  const [tbPageSize, setTbPageSize] = useState(100);
  const tradeBookParams = useMemo(() => {
    const p = { date: tbDate, status: tbStatus, page: tbPage, page_size: tbPageSize };
    if (tbSymbol) p.symbol = tbSymbol; // omit when All Symbols
    return p;
  }, [tbDate, tbStatus, tbSymbol, tbPage, tbPageSize]);
  const today = istToday();
  const tradeBookQ = useManualTradeBook(deploymentId, tradeBookParams, {
    enabled: tabsEnabled,
    refetchInterval: (tab === 'tradebook' && tbDate === today) ? 20_000 : false,
  });
  const tradeBookTotal = tradeBookQ.data?.total ?? 0;
  // De-duped watchlist + Trade Book symbols. Dynamic Option rows execute a resolved
  // option while retaining the base watchlist symbol, so expose both in filters.
  const tradeBookSymbols = useMemo(() => {
    const seen = new Set();
    const opts = [];
    const add = (value, label) => {
      const v = String(value || '').trim().toUpperCase();
      if (!v || seen.has(v)) return;
      seen.add(v);
      opts.push({ value: v, label: label || v });
    };
    for (const r of (wlQ.data || [])) add(r.symbol, r.display_symbol || r.symbol);
    for (const r of (tradeBookQ.data?.items || [])) {
      add(r.symbol, r.symbol);
      add(r.resolved_option_symbol, r.resolved_option_symbol);
      add(r.base_symbol || r.source_symbol, r.base_symbol || r.source_symbol);
    }
    return opts;
  }, [wlQ.data, tradeBookQ.data?.items]);

  // Current (non-terminal) risk-trigger lookups (by watchlist_row_id, and by symbol as fallback).
  // Allowlist: ACTIVE + PROCESSING count as current; all terminal statuses (CLOSED/FAILED/CANCELLED/…)
  // are excluded. First-wins.
  const { currentByRowId, currentBySymbol } = useMemo(() => {
    const byRow = {}, bySym = {};
    for (const t of (riskTriggersQ.data || [])) {
      const st = String(t.status || '').toUpperCase();
      if (st !== 'ACTIVE' && st !== 'PROCESSING') continue;
      if (t.watchlist_row_id != null && !(t.watchlist_row_id in byRow)) byRow[t.watchlist_row_id] = t;
      const s = String(t.symbol || '').trim().toUpperCase();
      if (s && !(s in bySym)) bySym[s] = t;
    }
    return { currentByRowId: byRow, currentBySymbol: bySym };
  }, [riskTriggersQ.data]);

  // Orders: shaped exactly like the global Paper Orderbook (shared normalizer).
  const orders = useMemo(() => (ordersQ.data || []).map((o, i) => {
    const r = normalizeOrderRow(o, i);
    // normalizeOrderRow drops the cancel-relevant fields → re-attach from the raw API row.
    const mode = String(o?.mode || '').toUpperCase();
    const conf = String(o?.confirmation_status || '').toUpperCase();
    const reqQty = Number(o?.quantity ?? r.qty) || 0;
    const filled = Number(o?.filled_quantity ?? r.filledQty) || 0;
    const pendingQty = Math.max(0, reqQty - filled);
    // Cancel only LIVE orders still AWAITING with pending qty. Never CONFIRMED_FILL/terminal/PAPER.
    const canCancel = mode === 'LIVE' && conf === 'AWAITING' && pendingQty > 0 && o?.broker_order_log_id != null;
    return { ...r, brokerOrderLogId: o?.broker_order_log_id ?? null, confirmationStatus: conf, mode, pendingQty, canCancel };
  }), [ordersQ.data]);
  // Positions: shaped like the global Paper Position Book + live-LTP/unrealized merge + exit/active-risk metadata.
  const allPositions = useMemo(
    () => (positionsQ.data || []).map((p, i) => buildPositionRow(p, i, ltpMap, symbolToRowId, currentByRowId, currentBySymbol)),
    [positionsQ.data, ltpMap, symbolToRowId, currentByRowId, currentBySymbol],
  );
  // Active (non-flat) rows only — drives the card-header "Open Positions: N" count and Exit-All eligibility.
  const openPositions = useMemo(() => allPositions.filter((p) => !p.isClosed), [allPositions]);

  // ── Exit All (batch square-off) — eligibility from ACTUAL open positions, not deployment status. ──
  const [exitAllOpen, setExitAllOpen] = useState(false);
  const exitAllSupported =
    String(strategy.executionMode || '').toUpperCase() === 'PAPER' &&
    ['UNDERLYING', 'OPTION'].includes(String(mt.trade_in || '').toUpperCase()) &&
    String(strategy.tradingMode || '').toUpperCase() === 'INTRADAY';
  const exitAllEnabled = exitAllSupported && openPositions.length > 0 && !exitAllOpen;
  const exitAllTitle = !exitAllSupported
    ? 'Exit All is available for Manual PAPER INTRADAY positions only'
    : openPositions.length === 0
      ? 'No open positions'
      : 'Exit all open PAPER positions';

  // ── Row-level broker Sync eligibility — normal Manual LIVE deployment only. Defensive category
  // check (the page already filters to Manual deployments). Excludes Manual Master / Master Parent /
  // Fanout Child / mapped-subscriber / PAPER. Per-row visibility additionally requires row.canExit
  // (an open position mapped to a watchlist row) — see PositionsTable.
  const isManualCategory = String(strategy.category || '').toUpperCase() === 'MANUAL_TRADING'
    || String(strategy.strategy_source || '').toUpperCase() === 'MANUAL';
  const isLiveDeployment = executionMode === 'LIVE';
  const brokerSyncEligible = isManualCategory && isLiveDeployment
    && !isMasterManual && !isMasterParent && !isFanoutChild
    && !isMappedOrSubscriber;

  // Positions tab data: the exact same set as `allPositions` (ALL current-day summary rows the
  // backend already returns — open + closed; no row added, dropped, or filtered here), reordered
  // for display only: open positions first, then closed; newest `openedAt` first within each
  // group; symbol as a deterministic tiebreak when `openedAt` is equal or missing. `openPositions`
  // (badge/Exit-All eligibility) is derived from `allPositions` above, before this sort — unaffected.
  const positionRows = useMemo(() => {
    const openedAtMs = (p) => {
      const t = p?.openedAt ? new Date(p.openedAt).getTime() : NaN;
      return Number.isFinite(t) ? t : -Infinity;
    };
    return [...allPositions].sort((a, b) => {
      const closedDiff = (a.isClosed ? 1 : 0) - (b.isClosed ? 1 : 0);
      if (closedDiff !== 0) return closedDiff;
      const timeDiff = openedAtMs(b) - openedAtMs(a);
      if (timeDiff !== 0) return timeDiff;
      return String(a.symbol || '').localeCompare(String(b.symbol || ''));
    });
  }, [allPositions]);
  // Card Total P&L: realized + unrealized across ALL current-day rows (same rule as the global Position
  // Book). Closed rows carry realized P&L with unrealized = 0, so realized is never double-counted.
  const totalPnl = allPositions.reduce(
    (sum, p) => sum + (Number(p.realizedPnl) || 0) + (Number(p.unrealizedPnl) || 0),
    0,
  );
  const pnlCls = totalPnl > 0 ? 'text-bull' : totalPnl < 0 ? 'text-bear' : 'text-txt-secondary';
  const ordersErr = ordersQ.isError ? (ordersQ.error?.message || 'Failed to load orders.') : null;
  const positionsErr = positionsQ.isError ? (positionsQ.error?.message || 'Failed to load positions.') : null;

  // ── Exit (row-based). PAPER = direct square-off; LIVE = prepare/confirm protected-LIMIT modal. ──
  const exitMut = useExitManualTradingPosition();
  const prepareExit = usePrepareManualTradingExit();
  const confirmExit = useConfirmManualTradingExit();
  const [exitingId, setExitingId] = useState(null);
  const [exitModal, setExitModal] = useState(null);   // { prepared, row }
  const [exitSubmitting, setExitSubmitting] = useState(false);
  const [exitError, setExitError] = useState(null);
  const exitPreparingRef = useRef(false);
  const exitConfirmingRef = useRef(false);

  const surfaceExitResult = (res) => {
    const c = classifyManualExitResult(res, { fallbackMode: strategy.executionMode });
    const fn = c.level === 'error' ? toast.error : c.level === 'warning' ? toast.warn : toast.success;
    fn(c.text);
  };

  const exitPosition = (row) => {
    if (!row?.exitRowId) { toast.error('Cannot exit: matching watchlist row not found.'); return; }
    // LIVE → prepare a protected LIMIT preview, then open the confirm modal (no order yet).
    if (normalizeMode(strategy.executionMode) === 'LIVE') {
      if (exitPreparingRef.current || prepareExit.isPending) return;
      exitPreparingRef.current = true;
      setExitError(null);
      toast.info('Preparing exit preview…');
      prepareExit.mutate(
        { rowId: row.exitRowId },
        {
          onSuccess: (prepared) => { exitPreparingRef.current = false; setExitModal({ prepared, row }); },
          onError: (e) => { exitPreparingRef.current = false; toast.error(`Could not prepare exit — ${extractOrderErrorMessage(e)}`); },
        },
      );
      return;
    }
    // PAPER → direct square-off (unchanged).
    setExitingId(row.id);
    exitMut.mutate(
      { rowId: row.exitRowId, deploymentId, payload: { client_request_id: genClientRequestId(), position_symbol: row.positionSymbol || row.symbol } },
      {
        onSuccess: (res) => { surfaceExitResult(res); setExitingId(null); },
        onError: (e) => { toast.error(extractOrderErrorMessage(e)); setExitingId(null); },
      },
    );
  };

  const closeExitModal = () => { setExitModal(null); setExitError(null); setExitSubmitting(false); };

  const confirmExitOrder = (limitPrice, priceMode) => {
    if (!exitModal?.prepared || !exitModal?.row?.exitRowId) return;
    if (exitConfirmingRef.current || confirmExit.isPending) return;
    exitConfirmingRef.current = true;
    setExitError(null);
    setExitSubmitting(true);
    confirmExit.mutate(
      {
        rowId: exitModal.row.exitRowId,
        deploymentId,
        payload: {
          client_request_id: exitModal.prepared.intent_id,
          prepared_token: exitModal.prepared.prepared_token,
          limit_price: String(limitPrice),
          price_mode: priceMode,
        },
      },
      {
        onSuccess: (res) => { exitConfirmingRef.current = false; closeExitModal(); surfaceExitResult(res); },
        onError: (e) => {
          exitConfirmingRef.current = false;
          const reason = extractOrderErrorMessage(e);
          // Tick/price validation rejected BEFORE dispatch → token still valid → keep modal open to correct.
          if (/tick|positive number|price or tick size/i.test(reason)) {
            setExitError(reason);
            setExitSubmitting(false);
            return;
          }
          // Expired / token invalid / broker rejection → intent spent → close and require a fresh prepare.
          closeExitModal();
          toast.error(/expired/i.test(reason) ? 'Prepared exit expired. Please prepare again.' : `Exit rejected — ${reason}`);
        },
      },
    );
  };

  // ── Row-level broker Sync (PREPARE/CONFIRM). One position at a time; `syncingRowId` disables
  //    ONLY the clicked row's Sync button. PREPARE never mutates state — a terminal (non-confirmation)
  //    status is just toasted. A confirmation-required status opens `syncModal`; CONFIRM applies it. ──
  const [syncingRowId, setSyncingRowId] = useState(null);
  const [syncModal, setSyncModal] = useState(null);   // { prepared, row, clientRequestId }
  const [syncSubmitting, setSyncSubmitting] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const syncGuardRef = useRef(false);
  const syncConfirmingRef = useRef(false);

  const closeSyncModal = () => { setSyncModal(null); setSyncError(null); setSyncSubmitting(false); };

  // Find the row result matching THIS row (BROKER_UNAVAILABLE responses can carry one result per
  // deployment watchlist row, not just the selected one).
  const findSyncRowResult = (res, row) => {
    const list = Array.isArray(res?.rows) ? res.rows : [];
    return list.find((r) => Number(r?.watchlist_row_id) === Number(row.exitRowId)) || list[0] || null;
  };

  const syncPosition = (row) => {
    if (!brokerSyncEligible || !row?.exitRowId) { toast.error('Cannot sync: matching watchlist row not found.'); return; }
    if (syncingRowId != null || syncGuardRef.current) return;
    syncGuardRef.current = true;
    setSyncingRowId(row.id);
    const clientRequestId = genClientRequestId();
    const body = {
      client_request_id: clientRequestId,
      watchlist_row_id: row.exitRowId,
      position_symbol: row.positionSymbol || row.symbol,
      sync_action: 'PREPARE',
    };
    if (row.entryBrokerOrderLogId != null) body.entry_broker_order_log_id = row.entryBrokerOrderLogId;
    reconcileBrokerMut.mutate(
      { deploymentId, body },
      {
        onSuccess: (res) => {
          syncGuardRef.current = false;
          setSyncingRowId(null);
          const result = findSyncRowResult(res, row);
          const status = String(result?.status || '').toUpperCase();
          if (status === 'CONFIRM_CLOSE' || status === 'CONFIRM_QUANTITY_UPDATE' || status === 'CONFIRM_REVERSED_CLOSE') {
            setSyncModal({ prepared: result, row, clientRequestId });
            return;
          }
          toastForBrokerSyncStatus(status, result?.message);
        },
        onError: (e) => {
          syncGuardRef.current = false;
          setSyncingRowId(null);
          toast.error(e?.message || 'Broker sync failed. Please try again.');
        },
      },
    );
  };

  // CONFIRM — sends back the PREPARE-issued token/action/expected quantities exactly as received;
  // never reconstructed here. `revisedQuantity` is only meaningful for UPDATE_QUANTITY.
  const confirmSyncPosition = (revisedQuantity) => {
    if (!syncModal?.prepared || !syncModal?.row?.exitRowId) return;
    if (syncConfirmingRef.current || reconcileBrokerMut.isPending) return;
    syncConfirmingRef.current = true;
    setSyncError(null);
    setSyncSubmitting(true);
    const { prepared, row, clientRequestId } = syncModal;
    const body = {
      client_request_id: clientRequestId,
      watchlist_row_id: row.exitRowId,
      position_symbol: row.positionSymbol || row.symbol,
      sync_action: 'CONFIRM',
      confirmation_token: prepared.confirmation_token,
      proposed_action: prepared.proposed_action,
      expected_local_signed_quantity: prepared.local_signed_quantity,
      expected_broker_signed_quantity: prepared.broker_signed_quantity,
    };
    if (row.entryBrokerOrderLogId != null) body.entry_broker_order_log_id = row.entryBrokerOrderLogId;
    if (prepared.proposed_action === 'UPDATE_QUANTITY') body.revised_quantity = revisedQuantity;
    reconcileBrokerMut.mutate(
      { deploymentId, body },
      {
        onSuccess: (res) => {
          syncConfirmingRef.current = false;
          const result = findSyncRowResult(res, row);
          const status = String(result?.status || '').toUpperCase();
          if (['SYNC_APPLIED', 'IN_SYNC', 'ALREADY_SYNCED', 'IMPORTED_EXTERNAL_EXIT'].includes(status)) {
            setSyncSubmitting(false);
            closeSyncModal();
            toastForBrokerSyncStatus(status, result?.message);
            invalidateManualBrokerSyncQueries(qc, deploymentId);
            refetchActiveManualBrokerSyncQueries(qc, deploymentId);
            return;
          }
          if (status === 'STALE_CONFIRMATION') {
            setSyncSubmitting(false);
            closeSyncModal();
            toast.error(result?.message || 'Broker position changed before confirmation. Please sync again.');
            invalidateManualBrokerSyncQueries(qc, deploymentId);
            return;
          }
          // Manual review / broker unavailable / projection retry — keep the modal open with an
          // inline error so the user can correct/retry without losing the confirmation context.
          setSyncSubmitting(false);
          setSyncError(result?.message || 'Broker sync could not be completed. Please try again.');
        },
        onError: (e) => {
          syncConfirmingRef.current = false;
          setSyncSubmitting(false);
          setSyncError(e?.message || 'Broker sync failed. Please try again.');
        },
      },
    );
  };

  // ── Cancel a pending LIVE order (prepare/confirm). Cancels pending qty only; offers the existing
  //    Manual Exit modal for any filled qty. No auto-exit. ──
  const prepareCancel = usePrepareManualTradingCancel();
  const confirmCancel = useConfirmManualTradingCancel();
  const [cancelModal, setCancelModal] = useState(null);   // { prepared }
  const [cancelResult, setCancelResult] = useState(null); // confirm response (result phase)
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const cancelPreparingRef = useRef(false);
  const cancelConfirmingRef = useRef(false);

  const closeCancelModal = () => { setCancelModal(null); setCancelResult(null); setCancelError(null); setCancelSubmitting(false); };

  const surfaceCancelResult = (c) => {
    const fn = c.level === 'error' ? toast.error : c.level === 'warning' ? toast.warn : c.level === 'info' ? toast.info : toast.success;
    fn(c.text);
  };

  const cancelOrder = (row) => {
    const id = row?.brokerOrderLogId;
    if (!id) { toast.error('Cannot cancel: order reference not found.'); return; }
    if (cancelPreparingRef.current || prepareCancel.isPending) return;
    cancelPreparingRef.current = true;
    setCancelingId(row.id);
    prepareCancel.mutate(
      { orderLogId: id },
      {
        onSuccess: (prepared) => {
          cancelPreparingRef.current = false;
          setCancelingId(null);
          // Stale row that became filled/cancelled/rejected → no modal; show reason + refresh.
          if (!prepared?.cancelable_local) {
            toast.warn(prepared?.reason_if_not_cancelable || 'Order is not cancelable.');
            ordersQ.refetch();
            return;
          }
          setCancelError(null);
          setCancelResult(null);
          setCancelModal({ prepared });
        },
        onError: (e) => { cancelPreparingRef.current = false; setCancelingId(null); toast.error(`Could not prepare cancel — ${extractOrderErrorMessage(e)}`); },
      },
    );
  };

  const confirmCancelOrder = () => {
    if (!cancelModal?.prepared?.broker_order_log_id) return;
    if (cancelConfirmingRef.current || confirmCancel.isPending) return;
    cancelConfirmingRef.current = true;
    setCancelError(null);
    setCancelSubmitting(true);
    confirmCancel.mutate(
      {
        orderLogId: cancelModal.prepared.broker_order_log_id,
        deploymentId,
        payload: {
          client_request_id: cancelModal.prepared.intent_id,
          prepared_token: cancelModal.prepared.prepared_token,
        },
      },
      {
        onSuccess: (res) => {
          cancelConfirmingRef.current = false;
          setCancelSubmitting(false);
          const c = classifyManualCancelResult(res);
          surfaceCancelResult(c);
          // Keep the modal open in result phase only to offer Exit for filled qty; else close.
          if (c.offerExit) setCancelResult(res);
          else closeCancelModal();
        },
        onError: (e) => { cancelConfirmingRef.current = false; closeCancelModal(); toast.error(`Cancel failed — ${extractOrderErrorMessage(e)}`); },
      },
    );
  };

  // "Exit Filled Position" → reuse the EXISTING Manual Exit prepare/confirm flow (no new exit function).
  const exitFilledFromCancel = (res) => {
    const rowId = res?.watchlist_row_id;
    if (!rowId) { toast.error('Cannot exit: matching watchlist row not found.'); return; }
    closeCancelModal();
    if (exitPreparingRef.current || prepareExit.isPending) return;
    exitPreparingRef.current = true;
    setExitError(null);
    toast.info('Preparing exit preview…');
    prepareExit.mutate(
      { rowId },
      {
        onSuccess: (prepared) => { exitPreparingRef.current = false; setExitModal({ prepared, row: { exitRowId: rowId } }); },
        onError: (e) => { exitPreparingRef.current = false; toast.error(`Could not prepare exit — ${extractOrderErrorMessage(e)}`); },
      },
    );
  };

  // ── Edit the OPEN position's ACTIVE risk trigger (E2.2). Sends ONE field only:
  // { target_trigger_price } or { stop_loss_trigger_price }. Empty → null (disable that side). ──
  const activeRiskMut = useUpdateManualActiveRisk();
  const editActiveRisk = (row, side /* 'target' | 'sl' */, rawVal) => {
    if (!row?.exitRowId) { toast.error('Cannot edit active risk: matching watchlist row not found.'); return; }
    const str = String(rawVal ?? '').trim();
    let value;
    if (str === '') {
      value = null; // cleared → disable this side
    } else {
      const n = Number(str);
      if (!Number.isFinite(n) || n < 0) { toast.error('Enter a valid non-negative price.'); return; }
      value = n;
    }
    const field = side === 'target' ? 'target_trigger_price' : 'stop_loss_trigger_price';
    activeRiskMut.mutate(
      { rowId: row.exitRowId, deploymentId, payload: { [field]: value, position_symbol: row.positionSymbol || row.symbol } },
      {
        onSuccess: () => toast.success(side === 'target' ? 'Active Target updated' : 'Active SL updated'),
        onError: (e) => toast.error(e?.message || 'Failed to update active risk.'),
      },
    );
  };

  const rows = useMemo(
    () => (wlQ.data || []).map((r) => {
      const row = mapWatchlistRow(r, signalOverrides);
      const symbolKey = String(row.symbol || '').trim().toUpperCase();
      const raw = ltpMap[symbolKey];
      const n = raw == null ? NaN : Number(raw); // accept numeric or numeric-string
      const withLtp = Number.isFinite(n) ? { ...row, ltp: n } : row;
      return { ...withLtp, quote: quotesMap[symbolKey] ?? null };
    }),
    [wlQ.data, signalOverrides, ltpMap, quotesMap],
  );

  // Wraps onAddSymbolSelect (== ManualTradingPage's addSymbol, unchanged) to also remember which
  // symbol to scroll/highlight once it lands in `rows` post-refetch — skipped entirely if the
  // symbol is already present (that's the 409/duplicate path, left alone: same toast, no scroll).
  const handleInlineAddSymbolSelect = (strategyWithRiskUnit, row) => {
    const symbol = String(row?.symbol || '').trim().toUpperCase();
    const alreadyPresent = symbol && rows.some((r) => String(r.symbol || '').trim().toUpperCase() === symbol);
    if (symbol && !alreadyPresent) setPendingAddedSymbol(symbol);
    onAddSymbolSelect(strategyWithRiskUnit, row);
  };

  const editCell = (row, field, value) => {
    const beField = FE_TO_BE[field];
    if (!beField || !row.backendId) return;
    updateMut.mutate(
      { rowId: row.backendId, deploymentId, payload: { [beField]: value } },
      { onError: (e) => toast.error(e?.message || 'Failed to update watchlist row.') },
    );
  };
  const deleteRow = (row) => {
    if (!row.backendId) return;
    deleteMut.mutate(
      { rowId: row.backendId, deploymentId },
      {
        onSuccess: () => toast.success(`${row.displaySymbol || row.symbol} removed from watchlist.`),
        onError: (e) => toast.error(e?.message || 'Failed to remove symbol.'),
      },
    );
  };
  // M5: gate the actual delete behind a confirm step — opens the modal only; deleteRow itself
  // (the mutation above) is untouched and runs only after the user confirms.
  const requestDeleteRow = (row) => {
    const blockingPosition = findBlockingOpenPositionForWatchlistRow(row, openPositions);
    if (blockingPosition) {
      toast.error('Cannot remove this symbol because an open position is running. Please exit/close the position first.');
      return;
    }
    setDeleteConfirmRow(row);
  };

  const watchlistInactive = !isActive;
  const watchlistError = wlQ.isError && wlQ.error?.status !== 400;

  // Presentational only: left status-rail color derived from the already-rendered
  // status value (LIVE/PAPER/other). No behavior or state logic.
  const mtRailCls = strategy.status === 'LIVE' ? 'mt-rail-live'
    : strategy.status === 'PAPER' ? 'mt-rail-paper'
    : 'mt-rail-stopped';

  return (
    <div className={cn('card mt-card', mtRailCls)}>
      {/* ── Header ── */}
      <div className="p-3 lg:p-4 border-b border-bg-border/60">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h3 className="font-display font-semibold text-sm text-txt-primary truncate">
                {strategy.strategyName}
              </h3>
              <StrategyStatusBadge status={strategy.status} />
              {canManageFanout && strategy.strategyModeKnown && !isFanoutChild && (
                isMasterManual && isMasterParent ? (
                  <span className="inline-flex items-center gap-1 badge border bg-amber/10 border-amber/40 text-amber">
                    <CheckCircle2 size={11} /> Manual Master Enabled
                  </span>
                ) : !isMasterManual ? (
                  <button
                    type="button"
                    disabled={enableMasterMut.isPending}
                    onClick={() => setEnableMasterConfirmOpen(true)}
                    className="inline-flex items-center gap-1 rounded border border-bg-border px-2 py-1 text-2xs font-mono font-medium text-txt-muted transition-colors hover:border-amber/40 hover:text-amber disabled:opacity-50"
                  >
                    {enableMasterMut.isPending ? <Spinner size={10} /> : <Plus size={11} />}
                    Enable as Master
                  </button>
                ) : null
              )}
              <span className="hidden lg:inline text-2xs font-mono text-txt-muted">
                {strategy.tradingMode} · {friendlyTradeIn(mt.trade_in)}
              </span>
            </div>

            {/* Strategy-level config (runtime values live on the watchlist rows) */}
            {/* Mobile: single compact row — meta on the left, colored P/L on the right. */}
            <div className="lg:hidden mt-1.5 flex items-center justify-between gap-2 font-mono">
              <span className="text-2xs text-txt-secondary truncate min-w-0">
                {[mt.exchange, friendlyProductType(mt.product_type), friendlyQtyMode(mt.qty_mode)].filter(Boolean).join(' · ')}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className={cn('text-sm font-semibold', pnlCls)}>₹{fmtPnl(totalPnl)}</span>
              </div>
            </div>

            {/* Full chips: desktop only. */}
            <div className="hidden lg:flex items-center gap-x-3 gap-y-1 flex-wrap mt-2">
              <MetaChip label="Exchange" value={mt.exchange} />
              <MetaChip label="Segment" value={friendlySegment(mt.segment)} />
              <MetaChip label="Product" value={friendlyProductType(mt.product_type)} />
              <MetaChip label="Qty Mode" value={friendlyQtyMode(mt.qty_mode)} />
              <MetaChip label="Pyramiding" value={mt.pyramiding ? 'On' : 'Off'} />
              <MetaChip label="Open Positions" value={openPositions.length} />
              <span className="mt-pnl inline-flex items-center gap-1.5 rounded-full border border-bg-border bg-bg-surface/70 px-2.5 py-1 text-sm font-mono font-semibold shadow-sm">
                <span className="text-txt-label">Total P&L:</span>
                <span className={cn('font-bold', pnlCls)}>₹{fmtPnl(totalPnl)}</span>
              </span>
              <button
                type="button"
                disabled={!isActive}
                onClick={() => onAddSymbol(strategy, riskUnit)}
                className="inline-flex btn-secondary items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={13} /> Add Symbol
              </button>
            </div>

            {/* Option Mapping summary (only when Trade In = Option) */}
            {mappingSummary && (
              <div className="mt-1.5 text-2xs font-mono text-txt-muted">
                <span className="text-txt-label">Option Mapping:</span>{' '}
                <span className="text-txt-secondary">{mappingSummary}</span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onToggle}
            className="btn-ghost !p-1.5 shrink-0"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-4 lg:p-4 lg:space-y-4">
          {/* ── Watchlist / Manual Signal Panel (always visible) ── */}
          <div>
            {/* Watchlist risk template vs. active-trade risk note. Desktop: always visible. */}
            {!isMobile && (
              <p className="text-2xs font-mono text-txt-muted mb-2">
                Watchlist Target/SL and Risk Unit are used for future entries. After entry, use Active Target and Active SL in Open Positions to modify the current trade.
              </p>
            )}

            {isActive && (
              <div className="lg:hidden mb-3">
                <InlineAddSymbolSearch
                  strategy={strategy}
                  riskUnit={riskUnit}
                  onAddSymbolSelect={handleInlineAddSymbolSelect}
                />
              </div>
            )}

            {watchlistInactive ? (
              <p className="text-2xs font-mono text-txt-muted py-6 text-center">
                Watchlist is available only for active Manual deployments.
              </p>
            ) : wlQ.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-txt-muted">
                <Spinner size={14} /> <span className="text-2xs font-mono">Loading watchlist…</span>
              </div>
            ) : watchlistError ? (
              <ErrorState message={wlQ.error?.message || 'Failed to load watchlist.'} onRetry={() => wlQ.refetch()} />
            ) : (
              <WatchlistTable
                strategy={strategy}
                rows={rows}
                riskUnit={riskUnit}
                openPositions={openPositions}
                onBuy={onBuy}
                onSell={onSell}
                onEditCell={editCell}
                onDelete={requestDeleteRow}
                onRowRiskUnitChange={changeRowRiskUnit}
                pendingAddedSymbol={pendingAddedSymbol}
                onPendingSymbolHandled={() => setPendingAddedSymbol(null)}
              />
            )}
          </div>

          {/* ── Orders / Positions / Trade Book — visible whenever the main card is expanded ── */}
          <div className="mt-blotter">
            <div className="flex items-center justify-between border-b border-bg-border mb-3">
              {/* no-scrollbar: keeps horizontal scroll for genuinely narrow screens without
                  rendering the browser's thin scrollbar chrome across the tab row. */}
              <div role="tablist" className="flex gap-1 overflow-x-auto no-scrollbar min-w-0">
                <TabButton
                  active={tab === 'positions'}
                  onClick={() => setTab('positions')}
                  label="Positions"
                  badgeCount={openPositions.length}
                />
                <TabButton
                  active={tab === 'orders'}
                  onClick={() => setTab('orders')}
                  label="Orders"
                  count={orders.length}
                />
                <TabButton
                  active={tab === 'tradebook'}
                  onClick={() => setTab('tradebook')}
                  label="Trade Book"
                  count={tradeBookTotal}
                />
              </div>
              {tab === 'positions' && (
                <div className="hidden lg:flex items-center gap-3 pb-2">
                  <span className="text-2xs font-mono">
                    <span className="text-txt-label">Total P&L: </span>
                    <span className={cn('font-medium', pnlCls)}>₹{fmtPnl(totalPnl)}</span>
                  </span>
                  {/* Exit All — batch square-off of all open PAPER positions (Manual UNDERLYING INTRADAY). */}
                  <button
                    type="button"
                    disabled={!exitAllEnabled}
                    title={exitAllTitle}
                    onClick={() => setExitAllOpen(true)}
                    className="btn-danger text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Exit All
                  </button>
                </div>
              )}
            </div>

            {tab === 'orders' && (
              <OrdersTable
                orders={orders}
                loading={ordersQ.isLoading}
                error={ordersErr}
                onRetry={() => ordersQ.refetch()}
                onCancelOrder={cancelOrder}
                cancelingId={cancelingId}
              />
            )}
            {tab === 'positions' && (
              <PositionsTable
                positions={positionRows}
                loading={positionsQ.isLoading}
                error={positionsErr}
                onRetry={() => positionsQ.refetch()}
                exitingId={exitingId}
                onExit={exitPosition}
                syncingId={syncingRowId}
                onSync={syncPosition}
                syncEnabled={brokerSyncEligible}
                onEditActiveRisk={editActiveRisk}
                totalPnl={totalPnl}
                exitAllEnabled={exitAllEnabled}
                exitAllTitle={exitAllTitle}
                onExitAllClick={() => setExitAllOpen(true)}
              />
            )}
            {tab === 'tradebook' && (
              <TradeBookTable
                data={tradeBookQ.data}
                loading={tradeBookQ.isLoading}
                error={tradeBookQ.isError ? (tradeBookQ.error?.message || 'Failed to load trade book.') : null}
                onRetry={() => tradeBookQ.refetch()}
                symbols={tradeBookSymbols}
                filters={{ date: tbDate, status: tbStatus, symbol: tbSymbol, pageSize: tbPageSize }}
                page={tbPage}
                maxDate={today}
                onDateChange={(d) => { setTbPage(1); setTbDate(d); }}
                onStatusChange={(s) => { setTbPage(1); setTbStatus(s); }}
                onSymbolChange={(s) => { setTbPage(1); setTbSymbol(s); }}
                onPageChange={setTbPage}
                onPageSizeChange={(n) => { setTbPage(1); setTbPageSize(n); }}
              />
            )}
          </div>

          {canManageFanout && isMasterManual && isMasterParent && (
            <ManualFanoutPanel strategyId={strategy.strategyId} />
          )}

        </div>
      )}

      {/* M5: Remove Symbol confirmation — deleteRow's mutation runs only after Confirm. */}
      <ConfirmModal
        open={enableMasterConfirmOpen}
        title="Enable as Manual Master?"
        message="This allows this manual strategy to fan out master orders to mapped child accounts when fan-out scope is selected."
        confirmLabel={enableMasterMut.isPending ? 'Enabling…' : 'Enable as Master'}
        cancelLabel="Cancel"
        onConfirm={async () => {
          if (enableMasterMut.isPending) return;
          try {
            await enableMasterMut.mutateAsync(strategy.strategyId);
            setEnableMasterConfirmOpen(false);
            toast.success('Manual Master enabled.');
          } catch (error) {
            toast.error(error?.message || 'Failed to enable Manual Master.');
          }
        }}
        onCancel={() => { if (!enableMasterMut.isPending) setEnableMasterConfirmOpen(false); }}
      />

      <ConfirmModal
        open={!!deleteConfirmRow}
        title="Remove symbol?"
        message="This will remove the symbol from this strategy watchlist and may clear its saved row settings such as quantity, target, stop loss, and order preferences."
        confirmLabel="Remove Symbol"
        cancelLabel="Keep Symbol"
        danger
        onConfirm={() => { deleteRow(deleteConfirmRow); setDeleteConfirmRow(null); }}
        onCancel={() => setDeleteConfirmRow(null)}
      />

      {/* Exit All confirm → processing → result (portaled; sends only { client_request_id }). */}
      <ExitAllModal
        open={exitAllOpen}
        strategy={strategy}
        openPositions={openPositions}
        deploymentId={deploymentId}
        onClose={() => setExitAllOpen(false)}
      />

      {/* LIVE exit prepare/confirm — protected LIMIT preview with editable price; server re-checks the
          broker position before any order. Mounted only when a preview is prepared. */}
      {exitModal && (
        <ConfirmManualExitModal
          ctx={exitModal}
          submitting={exitSubmitting}
          error={exitError}
          onConfirm={confirmExitOrder}
          onCancel={closeExitModal}
          onExpire={closeExitModal}
        />
      )}

      {/* LIVE order cancel prepare/confirm — cancels pending qty only; offers the existing Manual Exit
          modal for any filled qty (no auto-exit). Mounted only when a cancel preview is prepared. */}
      {cancelModal && (
        <ConfirmManualCancelOrderModal
          ctx={cancelModal}
          submitting={cancelSubmitting}
          error={cancelError}
          result={cancelResult}
          onConfirm={confirmCancelOrder}
          onCancel={closeCancelModal}
          onExitFilled={exitFilledFromCancel}
          onExpire={closeCancelModal}
        />
      )}

      {/* Row-level broker Sync CONFIRM — zero-quantity / quantity-mismatch / reverse-direction
          confirmation. Mounted only when a PREPARE call returned a confirmation-required status. */}
      {syncModal && (
        <ConfirmBrokerSyncModal
          ctx={syncModal}
          submitting={syncSubmitting}
          error={syncError}
          onConfirm={confirmSyncPosition}
          onCancel={closeSyncModal}
        />
      )}
    </div>
  );
}
